// src/tools/EncryptionKeysModal.jsx
import { useEffect, useState } from 'react';
import { Modal, Select, TextInput, PasswordInput, Button, Divider, Tabs, Text } from '@mantine/core';
import { get, post } from './Request';
import { useUser } from './Context';
import { showNotification } from '@mantine/notifications';

export default function KeyManagementModal({ opened, onClose, defaultTab = 'saved' }) {
  const [keys, setKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [newKeyName, setNewKeyName] = useState('');
  const [reload, setReload] = useState(true);
  const [tabValue, setTabValue] = useState(defaultTab); // controlled tab

  const { refreshPage } = useUser();

  // Sync defaultTab whenever the modal opens
  useEffect(() => {
    if (opened) {
      setTabValue(defaultTab);
    }
  }, [opened, defaultTab]);

  // Fetch keys
  useEffect(() => {
    if (!opened && !reload) return;

    const fetchKeys = async () => {
      try {
        const response = await get('key', undefined, localStorage.getItem('userToken'));
        setKeys(response || []);

        if (response?.length > 0) {
          setSelectedKey(response[0].key);

          // If keys exist and user wants "saved", open that tab.
          if (defaultTab === 'saved') {
            setTabValue('saved');
          }
        } else {
          setSelectedKey('');
          // If no keys exist, always force "add" tab
          setTabValue('add');
        }
      } catch (error) {
        console.error('Error fetching keys:', error);
      } finally {
        setReload(false);
      }
    };

    fetchKeys();
  }, [opened, reload, defaultTab]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      showNotification({
        title: 'Erreur',
        message: 'Le nom de la clé est requis',
        color: 'red',
        autoClose: 3000
      });
      return;
    }

    try {
      const response = await post('key', { name: newKeyName }, localStorage.getItem('userToken'));

      showNotification({
        title: 'Création de clé',
        message: 'La clé de chiffrement a été créée avec succès.',
        color: 'teal',
        autoClose: 3000
      });

      refreshPage(true);
      setNewKeyName('');
      setSelectedKey('');
      setReload(true);

      // After creating a key → go to saved keys tab
      setTabValue('saved');
    } catch (error) {
      console.error('Error creating key:', error);
      showNotification({
        title: 'Erreur',
        message: 'Échec de la création de la clé',
        color: 'red',
        autoClose: 3000
      });
    }
  };

  const selectedKeyObj = keys.find((k) => k.key === selectedKey);

  return (
    <Modal opened={opened} onClose={onClose} title="Clés de chiffrement" centered>
      <Tabs value={tabValue} onChange={setTabValue}>
        <Tabs.List mb="md">
          <Tabs.Tab value="saved">Clés enregistrées</Tabs.Tab>
          <Tabs.Tab value="add">Ajouter une clé</Tabs.Tab>
        </Tabs.List>

        {/* ------------ SAVED KEYS TAB ------------ */}
        <Tabs.Panel value="saved">
          {keys.length === 0 ? (
            <Text size="sm" c="dimmed" mb="sm">
              Aucune clé n&apos;est encore enregistrée.
            </Text>
          ) : (
            <>
              <Select
                label="Clé de chiffrement"
                placeholder="Sélectionner une clé"
                value={selectedKey}
                onChange={setSelectedKey}
                data={keys.map((key) => ({
                  value: key.key,
                  label: key.name
                }))}
                required
                mb="md"
              />

              {selectedKeyObj && (
                <>
                  <TextInput label="Identifiant de la clé" value={selectedKeyObj.id || 'N/A'} readOnly mb="sm" />
                  <PasswordInput label="Clé" placeholder="Clé de chiffrement sélectionnée" value={selectedKey} readOnly required mb="md" />
                </>
              )}
            </>
          )}
        </Tabs.Panel>

        <Divider my="md" />

        {/* ------------ ADD KEY TAB ------------ */}
        <Tabs.Panel value="add">
          <TextInput
            mt="xs"
            label="Nom de la nouvelle clé"
            placeholder="Entrer un nom pour la nouvelle clé"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            required
            mb="md"
          />
          <Button onClick={handleCreateKey} fullWidth>
            Créer la clé
          </Button>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
